import { useEffect, useMemo, useState } from "react";

import type { FeldWert, HerstellerUebersicht, KategorieKnoten, KonfigurationsZustand, ProduktInfo } from "@repo/shared";

import { api, errorMessage } from "../../lib/api";
import { euro } from "../../lib/regeln";
import { Blatt } from "../kalkulation/Blatt";
import { TextFeld, ZahlFeld } from "../kalkulation/Feld";
import { ProduktKonfigurator } from "../ProduktKonfigurator";

export type NeuePosition = {
  produktId: string;
  werte: Record<string, FeldWert>;
  menge: number;
  apartment: string | null;
  zimmer: string | null;
  konfigurationId: string | null;
  /** Gemeinsame Kennung zusammengehöriger Positionen, z. B. Vorhang und Schiene aus „Stoff & Stange“. */
  verknuepfung?: string | null;
};

/** Kategoriebaum flach, mit Tiefe für die Einrückung. */
function flach(knoten: KategorieKnoten[], tiefe = 0): { id: string; name: string; tiefe: number; anzahl: number }[] {
  return knoten.flatMap((k) => [{ id: k.id, name: k.name, tiefe, anzahl: k.anzahlProdukte }, ...flach(k.unterkategorien, tiefe + 1)]);
}

/** Werte, wie der Server sie zuletzt gemeldet hat – nur gesetzte. */
export function werteAus(stand: KonfigurationsZustand): Record<string, FeldWert> {
  const werte: Record<string, FeldWert> = {};
  for (const feld of stand.felder) {
    if (feld.zustand.wert != null && feld.zustand.wert !== "") werte[feld.schluessel] = feld.zustand.wert;
  }
  return werte;
}

/**
 * Position aus einem beliebigen Konfigurator: Hersteller, Kategorie, Produkt,
 * dann der normale Konfigurator mit Zuordnung zu Apartment und Zimmer.
 */
export function PositionHinzufuegen({
  offen, onClose, onHinzufuegen, vorbelegung,
}: {
  offen: boolean;
  onClose: () => void;
  onHinzufuegen: (p: NeuePosition) => Promise<string | null>;
  /** Apartment/Zimmer der letzten Position – spart Tipparbeit. */
  vorbelegung?: { apartment: string | null; zimmer: string | null };
}) {
  const [hersteller, setHersteller] = useState<HerstellerUebersicht[]>([]);
  const [herstellerKey, setHerstellerKey] = useState("");
  const [kategorien, setKategorien] = useState<KategorieKnoten[]>([]);
  const [kategorieId, setKategorieId] = useState("");
  const [suche, setSuche] = useState("");
  const [produkte, setProdukte] = useState<ProduktInfo[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [produkt, setProdukt] = useState<ProduktInfo | null>(null);
  const [stand, setStand] = useState<KonfigurationsZustand | null>(null);
  const [menge, setMenge] = useState(1);
  const [apartment, setApartment] = useState("");
  const [zimmer, setZimmer] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  /** Erster Schritt: alle Konfiguratoren als Karten; danach Katalog oder direkt der Konfigurator. */
  const [karten, setKarten] = useState(true);

  // Beim Öffnen: Hersteller laden, Zuordnung vorbelegen, Produkt zurücksetzen.
  useEffect(() => {
    if (!offen) return;
    setProdukt(null);
    setStand(null);
    setFehler(null);
    setKarten(true);
    setHerstellerKey("");
    setSuche("");
    setApartment(vorbelegung?.apartment ?? "");
    setZimmer(vorbelegung?.zimmer ?? "");
    void api.api.admin.katalog.hersteller.$get().then(async (res) => {
      if (!res.ok) return setFehler(await errorMessage(res, "Hersteller nicht ladbar"));
      const daten = await res.json();
      if ("hersteller" in daten) setHersteller(daten.hersteller);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  /** Karte gewählt: ein einziges Produkt öffnet direkt den Konfigurator, sonst der Katalog. */
  async function waehleHersteller(h: HerstellerUebersicht) {
    setFehler(null);
    setHerstellerKey(h.schluessel);
    if (h.anzahlProdukte === 1) {
      const res = await api.api.admin.katalog.hersteller[":hersteller"].produkte.$get({ param: { hersteller: h.schluessel }, query: { limit: "1" } });
      if (!res.ok) return setFehler(await errorMessage(res, "Produkt nicht ladbar"));
      const daten = await res.json();
      const einziges = "produkte" in daten ? daten.produkte[0] : undefined;
      if (einziges) {
        setStand(null);
        setProdukt(einziges);
        setKarten(false);
        return;
      }
    }
    setKarten(false);
  }

  const aktiverHersteller = hersteller.find((h) => h.schluessel === herstellerKey);

  useEffect(() => {
    setKategorien([]);
    setKategorieId("");
    if (!offen || !herstellerKey || !aktiverHersteller?.hatKategorien) return;
    void api.api.admin.katalog.hersteller[":hersteller"].kategorien.$get({ param: { hersteller: herstellerKey } }).then(async (res) => {
      if (!res.ok) return;
      const daten = await res.json();
      if ("kategorien" in daten) setKategorien(daten.kategorien);
    });
  }, [offen, herstellerKey, aktiverHersteller?.hatKategorien]);

  useEffect(() => {
    if (!offen || !herstellerKey) return;
    let aktiv = true;
    void api.api.admin.katalog.hersteller[":hersteller"].produkte
      .$get({ param: { hersteller: herstellerKey }, query: { kategorie: kategorieId || undefined, suche: suche || undefined, limit: "40" } })
      .then(async (res) => {
        if (!aktiv) return;
        if (!res.ok) return setFehler(await errorMessage(res, "Produkte nicht ladbar"));
        const daten = await res.json();
        if ("produkte" in daten) {
          setProdukte(daten.produkte);
          setGesamt(daten.gesamt);
          setFehler(null);
        }
      });
    return () => {
      aktiv = false;
    };
  }, [offen, herstellerKey, kategorieId, suche]);

  const kategorienFlach = useMemo(() => flach(kategorien), [kategorien]);
  const preis = stand?.ergebnis?.gueltig ? stand.ergebnis.preis : null;

  async function hinzufuegen() {
    if (!produkt || !stand || !preis) return;
    setSendet(true);
    const meldung = await onHinzufuegen({
      produktId: produkt.id,
      werte: werteAus(stand),
      menge,
      apartment: apartment.trim() || null,
      zimmer: zimmer.trim() || null,
      konfigurationId: stand.konfigurationId ?? null,
    });
    setSendet(false);
    if (meldung) setFehler(meldung);
    else onClose();
  }

  return (
    <Blatt offen={offen} onClose={onClose} titel={produkt ? `Position: ${produkt.name}` : karten ? "Konfigurator wählen" : `Produkt: ${aktiverHersteller?.name ?? ""}`}>
      {karten && !produkt ? (
        <div className="kalk-auswahl">
          {fehler && <p className="kalk-fehler">{fehler}</p>}
          <ul className="kalk-auswahl__liste objekt-konfiguratoren">
            {hersteller.map((h) => (
              <li key={h.schluessel}>
                <button type="button" className="kalk-auswahl__eintrag" onClick={() => void waehleHersteller(h)}>
                  <span className="kalk-auswahl__titel">{h.name}</span>
                  <span className="kalk-auswahl__meta">{h.beschreibung ?? ""}</span>
                  <span className="kalk-auswahl__zahlen"><span>{h.anzahlProdukte.toLocaleString("de-DE")} {h.anzahlProdukte === 1 ? "Produkt · direkt konfigurieren" : "Produkte"}</span></span>
                </button>
              </li>
            ))}
            {hersteller.length === 0 && !fehler && <li className="muted small">Hersteller werden geladen …</li>}
          </ul>
        </div>
      ) : !produkt ? (
        <div className="kalk-auswahl">
          <div className="kalk-auswahl__kopf objekt-produktwahl__kopf">
            <button type="button" className="button" onClick={() => { setKarten(true); setHerstellerKey(""); }}>← Konfiguratoren</button>
            <strong className="objekt-produktwahl__hersteller">{aktiverHersteller?.name}</strong>
            {kategorienFlach.length > 0 && (
              <select value={kategorieId} onChange={(e) => setKategorieId(e.target.value)} aria-label="Kategorie">
                <option value="">Alle Kategorien</option>
                {kategorienFlach.map((k) => (
                  <option key={k.id} value={k.id}>{"  ".repeat(k.tiefe)}{k.name} ({k.anzahl})</option>
                ))}
              </select>
            )}
            <input type="search" className="kalk-auswahl__suche" placeholder="Produkt suchen …" value={suche} onChange={(e) => setSuche(e.target.value)} autoFocus />
          </div>
          {fehler && <p className="kalk-fehler">{fehler}</p>}
          <ul className="kalk-auswahl__liste">
            {produkte.map((p) => (
              <li key={p.id}>
                <button type="button" className="kalk-auswahl__eintrag" onClick={() => { setStand(null); setFehler(null); setProdukt(p); }}>
                  <span className="kalk-auswahl__titel">{p.name}{p.variante && <small> {p.variante}</small>}</span>
                  <span className="kalk-auswahl__meta">{p.herstellerName} · {p.kategorieName}{p.beschreibung ? ` · ${p.beschreibung}` : ""}</span>
                </button>
              </li>
            ))}
            {produkte.length === 0 && !fehler && <li className="muted small">Keine Produkte gefunden.</li>}
          </ul>
          {gesamt > produkte.length && <p className="muted small">{gesamt} Treffer, die ersten {produkte.length} angezeigt. Suche eingrenzen.</p>}
        </div>
      ) : (
        <div className="objekt-konfi">
          <div className="objekt-konfi__leiste">
            <button type="button" className="button" onClick={() => { setProdukt(null); setStand(null); if (aktiverHersteller?.anzahlProdukte === 1) setKarten(true); }}>
              ← {aktiverHersteller?.anzahlProdukte === 1 ? "Konfiguratoren" : "Anderes Produkt"}
            </button>
            <div className="objekt-konfi__zuordnung">
              <TextFeld label="Apartment" wert={apartment} onChange={setApartment} platzhalter="App. 1" />
              <TextFeld label="Zimmer" wert={zimmer} onChange={setZimmer} platzhalter="Wohnen" />
              <ZahlFeld label="Menge" wert={menge} onChange={(v) => setMenge(Math.max(1, Math.round(v || 1)))} min={1} />
            </div>
            <button type="button" className="button kalk-knopf--primaer" disabled={!preis || sendet} onClick={() => void hinzufuegen()}>
              {sendet ? "Wird hinzugefügt …" : preis ? `Hinzufügen · ${euro(preis.vkNettoCents * menge)} netto` : "Noch kein Preis"}
            </button>
          </div>
          {fehler && <p className="kalk-fehler" role="alert">{fehler}</p>}
          <ProduktKonfigurator hersteller={herstellerKey} produktId={produkt.id} onErgebnis={setStand} />
        </div>
      )}
    </Blatt>
  );
}
